"use client";

import { RiCalendar2Line, RiCheckLine } from "@remixicon/react";
import type { Festival } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import { Spinner } from "@workspace/ui/components/spinner";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "@workspace/ui/components/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { Skeleton } from "@workspace/ui/components/skeleton";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useMemo, useState, Suspense } from "react";
import { useRelatedFestivals } from "@/hooks/features/festival/data/use-related-festivals";
import { useUserAttendedEditions } from "@/hooks/features/festival/data/use-user-attended-editions";
import type { RelatedFestivalRpcResponse } from "@/lib/data/festival/types";
import { cn } from "@/lib/utils";

interface FestivalVersionSelectorProps {
	currentFestival: Festival;
	className?: string;
	basePath?: "festivals" | "username"; // Which type of page we're on
	username?: string; // Only needed for username pages
	userId?: string; // Profile user ID — when set, filters editions to user-attended only
	isPageLoading?: boolean; // Whether the main page is loading
}

interface ParsedFestivalVersion {
	festival: RelatedFestivalRpcResponse;
	year: number;
	weekend: string | null;
	displayYear: string;
	displayWeekend: string;
}

// Parse festival versions into structured data
function parseFestivalVersions(
	festivals: RelatedFestivalRpcResponse[],
): ParsedFestivalVersion[] {
	return festivals.map((festival) => {
		const year = festival.year || (() => {
			const parts = festival.start_date.split("-");
			const y = Number(parts[0]);
			return y;
		})();
		const weekend = festival.weekend_identifier;

		return {
			festival,
			year,
			weekend,
			displayYear: year.toString(),
			displayWeekend: weekend || "",
		};
	});
}

// Group festivals by year
function groupByYear(
	versions: ParsedFestivalVersion[],
): Map<number, ParsedFestivalVersion[]> {
	const grouped = new Map<number, ParsedFestivalVersion[]>();

	for (const version of versions) {
		const existing = grouped.get(version.year) || [];
		grouped.set(version.year, [...existing, version]);
	}

	// Sort weekends within each year
	for (const [year, yearVersions] of grouped) {
		grouped.set(
			year,
			yearVersions.sort((a, b) => {
				// Extract numbers from weekend identifiers for proper sorting
				const aNum = Number.parseInt(a.weekend?.match(/\d+/)?.[0] || "0", 10);
				const bNum = Number.parseInt(b.weekend?.match(/\d+/)?.[0] || "0", 10);
				return aNum - bNum;
			}),
		);
	}

	return grouped;
}

function FestivalVersionSelectorInner({
	currentFestival,
	className,
	basePath = "festivals",
	username,
	userId,
	isPageLoading = false,
}: FestivalVersionSelectorProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [yearOpen, setYearOpen] = useState(false);
	const [weekendOpen, setWeekendOpen] = useState(false);
	const [isNavigating, setIsNavigating] = useState(false);

	// On profile pages (userId set), only show editions the user attended
	// On public festival pages, show all editions
	const { data: attendedEditions, isLoading: isLoadingAttended } =
		useUserAttendedEditions(
			userId ? currentFestival.id : null,
			userId ?? null,
		);
	const { data: allEditions, isLoading: isLoadingAll } = useRelatedFestivals(
		userId ? null : currentFestival.id,
	);

	const relatedFestivals = userId
		? (attendedEditions ?? [])
		: (allEditions ?? []);
	const isLoading = userId ? isLoadingAttended : isLoadingAll;

	// Parse and organize festival versions
	const {
		currentVersion,
		availableYears,
		availableWeekends,
		versionsByYear,
		weekendsForCurrentYear,
	} = useMemo(() => {
		const versions = parseFestivalVersions(
			relatedFestivals as RelatedFestivalRpcResponse[],
		);
		const current = versions.find((v) => v.festival.is_current);
		const grouped = groupByYear(versions);

		// Get unique years in descending order
		const years = Array.from(grouped.keys()).sort((a, b) => b - a);

		// Get ALL unique weekends across all years (for determining if we need weekend selector)
		const allWeekends = new Set<string>();
		for (const yearVersions of grouped.values()) {
			for (const version of yearVersions) {
				if (version.weekend) {
					allWeekends.add(version.weekend);
				}
			}
		}

		// Get weekends available for the current year only
		const currentYear = current?.year || new Date().getFullYear();
		const weekendsForYear = grouped.get(currentYear) || [];
		const currentYearWeekends = weekendsForYear
			.map((v) => v.weekend)
			.filter((w): w is string => w !== null);

		return {
			currentVersion: current,
			availableYears: years,
			availableWeekends: Array.from(allWeekends).sort((a, b) => {
				// Sort weekends numerically
				const aNum = Number.parseInt(a.match(/\d+/)?.[0] || "0", 10);
				const bNum = Number.parseInt(b.match(/\d+/)?.[0] || "0", 10);
				return aNum - bNum;
			}),
			weekendsForCurrentYear: currentYearWeekends,
			versionsByYear: grouped,
		};
	}, [relatedFestivals]);

	// Handle navigation to a different festival version
	const handleVersionChange = useCallback(
		async (targetSlug: string) => {
			// Preserve current URL parameters
			const currentParams = new URLSearchParams(searchParams?.toString() || "");

			// Show loading state
			setIsNavigating(true);

			// Build the new path
			const newPath =
				basePath === "username" && username
					? `/${username}/${targetSlug}`
					: `/festivals/${targetSlug}`;

			// Navigate to new festival preserving query params
			const queryString = currentParams.toString();
			const fullPath = queryString ? `${newPath}?${queryString}` : newPath;

			router.push(fullPath);
		},
		[basePath, username, router, searchParams],
	);

	// Handle year change
	const handleYearChange = useCallback(
		(year: number) => {
			const versionsForYear = versionsByYear.get(year) || [];

			if (versionsForYear.length === 0) return;

			// If there's only one version for this year, navigate directly
			if (versionsForYear.length === 1) {
				const firstVersion = versionsForYear[0];
				if (firstVersion) {
					handleVersionChange(firstVersion.festival.slug);
				}
				return;
			}

			// Otherwise, try to find a version with the same weekend as current
			const currentWeekend = currentVersion?.weekend;
			const matchingWeekend = versionsForYear.find(
				(v) => v.weekend === currentWeekend,
			);

			// Navigate to matching weekend or first available
			const targetVersion = matchingWeekend || versionsForYear[0];
			if (targetVersion) {
				handleVersionChange(targetVersion.festival.slug);
			}
		},
		[versionsByYear, currentVersion, handleVersionChange],
	);

	// Handle weekend change
	const handleWeekendChange = useCallback(
		(weekend: string) => {
			if (!currentVersion) return;

			const versionsForYear = versionsByYear.get(currentVersion.year) || [];
			const targetVersion = versionsForYear.find((v) => v.weekend === weekend);

			if (targetVersion) {
				handleVersionChange(targetVersion.festival.slug);
			}
		},
		[currentVersion, versionsByYear, handleVersionChange],
	);

	// Don't show selector if there's only one festival version
	if (
		!isLoading &&
		(relatedFestivals as RelatedFestivalRpcResponse[]).length <= 1
	) {
		return null;
	}

	// Hide completely during main page loading to prevent double loading states
	if (isPageLoading) {
		return null;
	}

	// Loading state - use skeleton instead of spinner
	if (isLoading) {
		return (
			<div className={cn("flex items-center gap-2", className)}>
				<Skeleton className="h-8 w-16" />
				<Skeleton className="h-8 w-12" />
			</div>
		);
	}

	const currentYear =
		currentVersion?.displayYear || new Date().getFullYear().toString();
	const currentWeekend = currentVersion?.displayWeekend || "";
	const hasMultipleWeekends = availableWeekends.length > 1;

	return (
		<div className={cn("flex items-center gap-2", className)}>
			{/* Year Selector */}
			<Popover open={yearOpen} onOpenChange={setYearOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="secondary"
						size="sm"
						className={cn(
							"inline-flex items-center text-sm",
							isNavigating && "pointer-events-none opacity-50",
						)}
					>
						{isNavigating ? (
							<Spinner className="mr-1 h-4 w-4 animate-spin" />
						) : (
							<RiCalendar2Line className="mr-1 h-4 w-4" />
						)}
						<span>{currentYear}</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[200px] p-0" align="end">
					<Command>
						<CommandInput placeholder="Search year..." />
						<CommandEmpty>No year found.</CommandEmpty>
						<CommandGroup>
							{availableYears.map((year) => (
								<CommandItem
									key={year}
									value={year.toString()}
									onSelect={() => {
										handleYearChange(year);
										setYearOpen(false);
									}}
								>
									{year}
									{year === currentVersion?.year && (
										<RiCheckLine className="ml-auto h-4 w-4" />
									)}
								</CommandItem>
							))}
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>

			{/* Weekend Selector - only show if multiple weekends exist and current year has multiple weekends */}
			{hasMultipleWeekends && weekendsForCurrentYear.length > 1 && (
				<Popover open={weekendOpen} onOpenChange={setWeekendOpen}>
					<PopoverTrigger asChild>
						<Button
							variant="secondary"
							size="sm"
							className={cn(
								"text-sm",
								isNavigating && "pointer-events-none opacity-50",
							)}
						>
							{isNavigating ? (
								<Spinner className="h-4 w-4 animate-spin" />
							) : (
								currentWeekend.replace("Weekend ", "W")
							)}
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-[200px] p-0" align="end">
						<Command>
							<CommandGroup>
								{weekendsForCurrentYear.map((weekend) => (
									<CommandItem
										key={weekend}
										value={weekend}
										onSelect={() => {
											handleWeekendChange(weekend);
											setWeekendOpen(false);
										}}
									>
										{weekend}
										{weekend === currentWeekend && (
											<RiCheckLine className="ml-auto h-4 w-4" />
										)}
									</CommandItem>
								))}
							</CommandGroup>
						</Command>
					</PopoverContent>
				</Popover>
			)}
		</div>
	);
}

export function FestivalVersionSelector(props: FestivalVersionSelectorProps) {
	return (
		<Suspense fallback={<div className={cn("flex items-center gap-2", props.className)}><Skeleton className="h-8 w-16" /><Skeleton className="h-8 w-12" /></div>}>
			<FestivalVersionSelectorInner {...props} />
		</Suspense>
	);
}
